import type { QueryClient } from '@tanstack/react-query';

import { getStatementReferencesV2QueryOptions, statementReferencesQueryKey } from './queryOptions';
import type { StatementReferenceState } from '../model/statementReferenceState';

/**
 * Writes to a conversation's cached statement references, so the hooks patch
 * one key one way. Each routine takes the account its hook reads from
 * `useUserScope()`, the account the query is keyed by.
 */
export function putStatementReferenceState(
  queryClient: QueryClient,
  userId: string | undefined,
  conversationId: string,
  state: StatementReferenceState
) {
  queryClient.setQueryData(
    getStatementReferencesV2QueryOptions(userId, conversationId).queryKey,
    (previous) => {
      const others = (previous ?? []).filter((entry) => entry.messageSid !== state.messageSid);

      return [...others, state];
    }
  );
}

/**
 * Applies a patch to a message already in the cache, for an optimistic
 * write, and returns a function that puts the replaced list back for a
 * failed one.
 * A message not in the cache is left alone: it has no bubble to update.
 */
export function patchStatementReferenceState(
  queryClient: QueryClient,
  userId: string | undefined,
  conversationId: string,
  messageSid: string,
  patch: Partial<StatementReferenceState>
) {
  const { queryKey } = getStatementReferencesV2QueryOptions(userId, conversationId);

  const previous = queryClient.getQueryData(queryKey);

  queryClient.setQueryData(queryKey, (current) =>
    current?.map((entry) => (entry.messageSid === messageSid ? { ...entry, ...patch } : entry))
  );

  return () => queryClient.setQueryData(queryKey, previous);
}

/** Every account's copy, since an announcement names only the conversation. */
export function refetchStatementReferences(queryClient: QueryClient, conversationId: string) {
  return queryClient.invalidateQueries({ queryKey: statementReferencesQueryKey(conversationId) });
}
